<template>
    <main class="task-progress-page mx-2 mt-3 pb-8 text-base-content">
        <section class="mx-auto max-w-7xl">
            <div class="mb-4 rounded-[24px] border border-base-200/80 bg-base-100/90 p-4 shadow-sm backdrop-blur sm:p-5">
                <div class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div class="min-w-0">
                        <button class="btn btn-ghost btn-sm -ml-3 rounded-full" @click="goBack">
                            {{ $tt('返回操作记录') }}
                        </button>
                        <div class="mt-3 flex flex-wrap items-center gap-2">
                            <h1 class="break-words text-2xl font-semibold leading-tight sm:text-3xl">
                                {{ $tt(progress.action || '任务监控') }}
                            </h1>
                            <span class="badge border-0 px-3 py-3 font-medium" :class="statusBadgeClass">{{ statusText }}</span>
                        </div>
                        <div class="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-base-content/60">
                            <span class="font-mono tabular-nums">{{ $tt('任务 ID: {id}', { id: id }) }}</span>
                            <span class="inline-flex items-center gap-2">
                                <span class="h-2 w-2 rounded-full" :class="connectionDotClass"></span>
                                {{ connectionText }}
                            </span>
                        </div>
                    </div>

                    <div class="flex flex-wrap items-center gap-2">
                        <button
                            v-if="progress.status === 'running'"
                            class="btn btn-outline btn-error btn-sm rounded-full"
                            @click="handleStop"
                        >{{ $tt('停止') }}</button>
                        <button
                            v-if="progress.status === 'stop' && canResume"
                            class="btn btn-outline btn-success btn-sm rounded-full"
                            @click="handleResume"
                        >{{ $tt('恢复') }}</button>
                        <button class="btn btn-primary btn-sm rounded-full" @click="viewDetails">
                            {{ $tt('查看明细') }}
                        </button>
                    </div>
                </div>
            </div>

            <div v-if="isLoading" class="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
                <div class="min-h-[360px] animate-pulse rounded-[28px] bg-base-200/80"></div>
                <div class="space-y-4">
                    <div class="h-36 animate-pulse rounded-[24px] bg-base-200/80"></div>
                    <div class="h-48 animate-pulse rounded-[24px] bg-base-200/80"></div>
                </div>
            </div>

            <div v-else-if="loadError" class="rounded-[24px] border border-error/30 bg-error/10 p-6">
                <div class="font-medium text-error">{{ $tt('无法加载任务进度') }}</div>
                <p class="mt-2 text-sm leading-6 text-base-content/65">{{ loadError }}</p>
                <button class="btn btn-sm mt-5 rounded-full" @click="fetchProgress">{{ $tt('重新加载') }}</button>
            </div>

            <div v-else class="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
                <section class="task-progress-panel min-w-0 rounded-[28px] border border-base-200/80 bg-base-100 p-5 shadow-sm sm:p-6">
                    <div class="grid gap-6 xl:grid-cols-[minmax(0,1fr)_240px] xl:items-center">
                        <div class="min-w-0">
                            <div class="text-sm font-medium text-base-content/60">{{ $tt('完成进度') }}</div>
                            <div class="mt-3 flex flex-wrap items-end gap-x-3 gap-y-1">
                                <span class="font-mono text-5xl font-semibold leading-none tabular-nums sm:text-6xl">{{ progress.completed || 0 }}</span>
                                <span class="pb-1 font-mono text-lg text-base-content/45 tabular-nums">/ {{ progress.total || 0 }}</span>
                            </div>
                            <div class="mt-6">
                                <div class="h-3 overflow-hidden rounded-full bg-base-200">
                                    <div
                                        class="h-full rounded-full bg-primary transition-[width] duration-500"
                                        :style="{ width: `${progressPercent}%` }"
                                    ></div>
                                </div>
                                <div class="mt-3 flex justify-between text-sm text-base-content/60">
                                    <span>{{ $tt('当前进度') }}</span>
                                    <span class="font-mono font-medium tabular-nums text-base-content">{{ progressPercent }}%</span>
                                </div>
                            </div>
                        </div>

                        <div class="flex items-center justify-center xl:justify-end">
                            <div
                                class="progress-orb"
                                :style="{ '--progress': `${progressPercent}%` }"
                                role="img"
                                :aria-label="$tt('完成进度百分比')"
                            >
                                <div class="progress-orb-inner">
                                    <span class="font-mono text-4xl font-semibold tabular-nums">{{ progressPercent }}</span>
                                    <span class="mt-1 text-xs font-medium text-base-content/50">%</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                        <div
                            v-for="stat in progressStats"
                            :key="stat.label"
                            class="rounded-[22px] bg-base-200/55 p-4"
                        >
                            <div class="text-sm text-base-content/55">{{ $tt(stat.label) }}</div>
                            <div class="mt-2 font-mono text-3xl font-semibold tabular-nums" :class="stat.className">
                                {{ stat.value }}
                            </div>
                        </div>
                    </div>
                </section>

                <aside class="grid gap-4">
                    <section class="rounded-[24px] border border-base-200/80 bg-base-100 p-5 shadow-sm">
                        <div class="flex items-start justify-between gap-4">
                            <div>
                                <div class="text-sm font-medium text-base-content/60">{{ $tt('总耗时') }}</div>
                                <div class="mt-2 font-mono text-3xl font-semibold tabular-nums">{{ elapsedText }}</div>
                            </div>
                            <span class="badge border-0 px-3 py-3 font-medium" :class="statusBadgeClass">{{ statusText }}</span>
                        </div>
                        <div class="mt-5 grid gap-3 text-sm text-base-content/60">
                            <div>
                                <div class="text-xs text-base-content/45">{{ $tt('开始时间') }}</div>
                                <div class="mt-1 font-mono text-base-content break-all">{{ progress.started_at || '-' }}</div>
                            </div>
                            <div>
                                <div class="text-xs text-base-content/45">{{ $tt('结束时间') }}</div>
                                <div class="mt-1 font-mono text-base-content break-all">{{ progress.completed_at || '-' }}</div>
                            </div>
                        </div>
                    </section>

                    <section class="rounded-[24px] border border-base-200/80 bg-base-100 p-5 shadow-sm">
                        <div class="flex flex-wrap items-center justify-between gap-3">
                            <div class="text-sm font-medium text-base-content/60">{{ $tt('当前文件') }}</div>
                            <span
                                v-if="progress.current_status"
                                class="badge shrink-0 border-0 px-3 py-3 font-medium"
                                :class="detailStatusBadgeClass"
                            >{{ currentStatusText }}</span>
                        </div>
                        <div v-if="progress.current_file" class="mt-4 break-all rounded-[18px] bg-base-200/55 p-4 font-mono text-sm leading-6">
                            {{ progress.current_file }}
                        </div>
                        <div v-else class="mt-4 rounded-[18px] border border-dashed border-base-300 bg-base-100 px-4 py-6 text-center text-sm text-base-content/50">
                            {{ $tt('等待任务开始处理') }}
                        </div>
                    </section>
                </aside>
            </div>
        </section>
    </main>
</template>

<script>
import { createTaskLogWs } from '@/utils/syncRoomWs'

const TASK_NAME_ALIASES = {
    autoScrape: ['自动刮削', 'auto scrape', 'auto-scrape', 'auto_scrape'],
    importLibrary: ['导入收藏', 'import library', 'import-collection', 'import_collection']
}

export default {
    name: 'TaskProgress',
    data() {
        return {
            id: this.$route.params.id,
            progress: {},
            isLoading: true,
            loadError: '',
            taskLogWs: null,
            reconnectTimer: null,
            elapsedTimer: null,
            isConnected: false,
            isDestroying: false,
            nowTick: Date.now()
        }
    },
    computed: {
        progressPercent() {
            const total = Number(this.progress.total || 0)
            if (!total) return 0
            return Math.min(100, Math.round((Number(this.progress.completed || 0) / total) * 100))
        },
        elapsedText() {
            const startTime = this.parseStartedAt(this.progress.started_at)
            if (!startTime) {
                return this.formatElapsed(0)
            }
            // 任务已结束：结束时间 - 开始时间（固定）；运行中：当前时间 - 开始时间（实时）
            if (this.isTaskFinished(this.progress.status)) {
                const completedAt = this.parseStartedAt(this.progress.completed_at)
                if (completedAt > startTime) {
                    return this.formatElapsed(Math.max(0, Math.floor((completedAt - startTime) / 1000)))
                }
            }
            return this.formatElapsed(Math.max(0, Math.floor((this.nowTick - startTime) / 1000)))
        },
        statusText() {
            return this.getStatusText(this.progress.status)
        },
        currentStatusText() {
            return this.getDetailStatusText(this.progress.current_status)
        },
        statusBadgeClass() {
            const map = {
                running: 'bg-info/10 text-info',
                wait: 'bg-info/10 text-info',
                success: 'bg-success/10 text-success',
                complete: 'bg-success/10 text-success',
                stop: 'bg-warning/20 text-warning',
                fail: 'bg-error/10 text-error'
            }
            return map[this.progress.status] || 'bg-base-200 text-base-content/65'
        },
        detailStatusBadgeClass() {
            const status = this.progress.current_status
            if (['fail', 'false_file', 'metadata_error', 'timeout', 'scrape_failed'].indexOf(status) !== -1) {
                return 'bg-error/10 text-error'
            }
            if (['running', 'wait'].indexOf(status) !== -1) {
                return 'bg-info/10 text-info'
            }
            return 'bg-success/10 text-success'
        },
        progressStats() {
            return [
                { label: '成功', value: this.progress.success || 0, className: 'text-success' },
                { label: '失败', value: this.progress.failed || 0, className: 'text-error' },
                { label: '运行中', value: this.progress.running || 0, className: 'text-info' },
                { label: '等待', value: this.progress.waiting || 0, className: 'text-base-content' }
            ]
        },
        connectionText() {
            if (this.isTaskFinished(this.progress.status)) {
                return this.$tt('已归档')
            }
            return this.isConnected ? this.$tt('实时同步') : this.$tt('等待同步')
        },
        connectionDotClass() {
            if (this.isTaskFinished(this.progress.status)) {
                return 'bg-base-content/30'
            }
            return this.isConnected ? 'bg-success' : 'bg-warning'
        },
        canResume() {
            const action = this.normalizeTaskName(this.progress.action)
            return Object.keys(TASK_NAME_ALIASES).some((type) =>
                TASK_NAME_ALIASES[type].some((alias) => this.normalizeTaskName(alias) === action)
            )
        }
    },
    mounted() {
        this.fetchProgress()
        this.elapsedTimer = setInterval(() => {
            // 更新响应式 nowTick，使 elapsedText computed 每秒重新求值
            this.nowTick = Date.now()
        }, 1000)
    },
    beforeDestroy() {
        this.isDestroying = true
        this.closeTaskLogWs()
        clearInterval(this.elapsedTimer)
    },
    methods: {
        async fetchProgress() {
            this.isLoading = true
            this.loadError = ''
            try {
                const res = await this.$api.Task.getAuditLogProgress({ id: this.id })
                if (!res.result) {
                    throw new Error(res.message || this.$tt('服务器未返回任务进度'))
                }
                this.applyProgress(res.data || {})
                // 已结束的记录无需保持 WebSocket 连接；运行中才建立连接
                this.syncWsConnection()
            } catch (error) {
                this.loadError = error.message || this.$tt('网络错误或服务器异常')
            } finally {
                this.isLoading = false
            }
        },
        syncWsConnection() {
            if (this.isTaskFinished(this.progress.status)) {
                this.closeTaskLogWs()
            } else {
                this.connectTaskLogWs()
            }
        },
        isTaskFinished(status) {
            return ['success', 'complete', 'fail', 'stop'].indexOf(status || '') !== -1
        },
        applyProgress(payload) {
            if (!payload || String(payload.log_id) !== String(this.id)) {
                return
            }
            this.progress = { ...this.progress, ...payload }
        },
        connectTaskLogWs() {
            this.closeTaskLogWs()
            // 已结束的任务不需要 WebSocket
            if (this.isTaskFinished(this.progress.status)) {
                return
            }
            this.taskLogWs = createTaskLogWs(this.id, {
                onOpen: () => {
                    this.isConnected = true
                },
                onClose: () => {
                    this.isConnected = false
                    if (!this.isDestroying && !this.isTaskFinished(this.progress.status)) {
                        this.reconnectTimer = setTimeout(() => {
                            this.connectTaskLogWs()
                            this.fetchProgress()
                        }, 2000)
                    }
                },
                onError: () => {
                    this.isConnected = false
                },
                onMessage: (msg) => {
                    if (msg && msg.event === 'task_progress.updated') {
                        this.applyProgress(msg.data || {})
                        // 任务结束后主动断开连接
                        if (this.isTaskFinished(this.progress.status)) {
                            this.closeTaskLogWs()
                        }
                    }
                }
            })
        },
        closeTaskLogWs() {
            if (this.reconnectTimer) {
                clearTimeout(this.reconnectTimer)
                this.reconnectTimer = null
            }
            if (this.taskLogWs) {
                this.taskLogWs.close()
                this.taskLogWs = null
            }
        },
        normalizeTaskName(name) {
            return String(name || '').trim().toLowerCase().replace(/[_-]/g, ' ').replace(/\s+/g, ' ')
        },
        parseStartedAt(value) {
            if (!value) return 0
            // 后端返回格式: YYYY-MM-DD HH:MM:SS，服务器时区为 Asia/Shanghai
            const match = String(value).match(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})/)
            if (!match) return 0
            const timestamp = new Date(`${match[1]}T${match[2]}+08:00`).getTime()
            return Number.isNaN(timestamp) ? 0 : timestamp
        },
        getStatusText(status) {
            const map = {
                running: '进行中',
                wait: '等待',
                success: '成功',
                complete: '完成',
                stop: '已停止',
                fail: '失败'
            }
            return this.$tt(map[status] || status || '-')
        },
        getDetailStatusText(status) {
            const map = {
                wait: '等待处理',
                running: '正在处理',
                success: '已完成',
                completed: '已完成',
                fail: '处理失败',
                false_file: '虚假文件格式',
                metadata_error: '元数据错误',
                timeout: '处理超时',
                scrape_exact: '完全匹配',
                scrape_partial: '模式匹配',
                scrape_failed: '刮削失败'
            }
            return this.$tt(map[status] || status || '-')
        },
        formatElapsed(seconds) {
            const total = Math.max(0, Number(seconds || 0))
            const hours = Math.floor(total / 3600)
            const minutes = Math.floor((total % 3600) / 60)
            const secs = total % 60
            if (hours) return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
            return `${minutes}:${String(secs).padStart(2, '0')}`
        },
        goBack() {
            this.$router.push({ path: '/log' })
        },
        viewDetails() {
            this.$router.push({ path: `/log_detail/${this.id}/` })
        },
        handleStop() {
            this.$api.Task.stopAuditLogDetail({ id: this.id }).then((res) => {
                if (res.result) {
                    this.$myMsg.notify({ title: this.$tt('停止成功'), type: 'success', time: 2000 })
                } else {
                    this.$myMsg.notify({ title: this.$tt('停止失败'), content: res.message, type: 'error', time: 2000 })
                }
            })
        },
        handleResume() {
            this.$api.Task.resumeAuditLog({ id: this.id }).then((res) => {
                if (res.result) {
                    this.$myMsg.notify({ title: this.$tt('恢复成功'), type: 'success', time: 2000 })
                } else {
                    this.$myMsg.notify({ title: this.$tt('恢复失败'), content: res.message, type: 'error', time: 2000 })
                }
            })
        }
    }
}
</script>

<style scoped>
.task-progress-page {
    min-height: calc(100dvh - 1rem);
}

.task-progress-panel {
    background:
        radial-gradient(circle at 12% 0%, oklch(var(--p) / 0.12), transparent 30rem),
        oklch(var(--b1));
}

.progress-orb {
    --progress: 0%;
    width: min(56vw, 220px);
    aspect-ratio: 1;
    border-radius: 999px;
    display: grid;
    place-items: center;
    background:
        conic-gradient(oklch(var(--p)) var(--progress), oklch(var(--b2)) 0),
        oklch(var(--b2));
    box-shadow: inset 0 0 0 1px oklch(var(--bc) / 0.08);
}

.progress-orb-inner {
    width: 74%;
    aspect-ratio: 1;
    border-radius: 999px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: oklch(var(--b1));
    box-shadow:
        inset 0 1px 0 oklch(var(--bc) / 0.08),
        0 18px 50px oklch(var(--bc) / 0.08);
}

@media (prefers-reduced-motion: reduce) {
    .task-progress-page * {
        transition-duration: 0.01ms !important;
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
    }
}
</style>
